/**
 * WebAuthn / Biometric Authentication Utilities
 * 
 * Provides functions for:
 * - Checking browser support for WebAuthn
 * - Registering biometric credentials
 * - Authenticating with biometric credentials
 */

/**
 * Check if the browser supports WebAuthn (biometric authentication)
 * @returns {boolean}
 */
export function isBiometricSupported() {
  return window.PublicKeyCredential !== undefined &&
    typeof window.PublicKeyCredential === 'function';
}

/**
 * Check if the device has platform authenticator (fingerprint/face recognition)
 * @returns {Promise<boolean>}
 */
export async function isPlatformAuthenticatorAvailable() {
  if (!isBiometricSupported()) return false;
  
  try {
    return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch (e) {
    return false;
  }
}

/**
 * Convert ArrayBuffer to Base64url string
 */
function arrayBufferToBase64url(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
}

/**
 * Convert Base64url string to ArrayBuffer
 */
function base64urlToArrayBuffer(base64url) {
  const base64 = base64url
    .replace(/-/g, '+')
    .replace(/_/g, '/')
    .padEnd(base64url.length + (4 - (base64url.length % 4)) % 4, '=');
  
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

/**
 * Prepare registration options from server response for navigator.credentials.create()
 */
function prepareRegistrationOptions(serverOptions) {
  return {
    challenge: base64urlToArrayBuffer(serverOptions.challenge),
    rp: {
      id: serverOptions.rpId,
      name: serverOptions.rpName,
    },
    user: {
      id: base64urlToArrayBuffer(serverOptions.userId),
      name: serverOptions.userName,
      displayName: serverOptions.userDisplayName,
    },
    pubKeyCredParams: [
      { alg: -7, type: 'public-key' },   // ES256 (ECDSA w/ SHA-256)
      { alg: -257, type: 'public-key' }, // RS256 (RSASSA-PKCS1-v1_5 w/ SHA-256)
    ],
    authenticatorSelection: {
      authenticatorAttachment: 'platform', // Use built-in authenticator (fingerprint/face)
      userVerification: 'required',
      residentKey: 'preferred',
      requireResidentKey: false,
    },
    attestation: serverOptions.attestation || 'none',
    timeout: serverOptions.timeout || 120000,
    extensions: serverOptions.extensions || { credProps: true },
    // Devices already registered for this user: the browser refuses to create a duplicate
    excludeCredentials: (serverOptions.excludeCredentials || []).map(cred => ({
      type: cred.type || 'public-key',
      id: base64urlToArrayBuffer(cred.id),
      transports: cred.transports || ['internal'],
    })),
  };
}

/**
 * Turn a navigator.credentials error into an Error with a useful message.
 * SecurityError almost always means the server's rpId does not match the address in the browser bar,
 * so say which two values disagree. The raw DOMException name is kept on `error.code` and logged.
 */
function toBiometricError(action, error, rpId) {
  const host = window.location.hostname;
  console.error(`[biometric] ${action} failed`, { name: error?.name, message: error?.message, rpId, host });

  let message;
  switch (error?.name) {
    case 'NotAllowedError':
      message = `Biometric ${action} was cancelled or not allowed`;
      break;
    case 'SecurityError':
      message = rpId && host !== rpId && !host.endsWith(`.${rpId}`)
        ? `Biometric ${action} is set up for "${rpId}" but you are on "${host}". Open the app from the official address.`
        : window.isSecureContext
          ? `Biometric ${action} failed for security reasons (domain mismatch)`
          : `Biometric ${action} needs a secure (https) connection`;
      break;
    case 'InvalidStateError':
      message = 'This device is already registered for biometric sign-in';
      break;
    case 'NotSupportedError':
      message = 'Your device does not support this biometric method';
      break;
    default:
      message = `Biometric ${action} failed: ${error?.message || error?.name || 'unknown error'}`;
  }
  const err = new Error(message);
  err.code = error?.name;
  return err;
}

/**
 * Prepare authentication options from server response for navigator.credentials.get()
 */
function prepareAuthenticationOptions(serverOptions) {
  const options = {
    challenge: base64urlToArrayBuffer(serverOptions.challenge),
    rpId: serverOptions.rpId,
    timeout: serverOptions.timeout || 120000,
    userVerification: serverOptions.userVerification || 'required',
  };

  if (serverOptions.allowCredentials && serverOptions.allowCredentials.length > 0) {
    options.allowCredentials = serverOptions.allowCredentials.map(cred => ({
      type: cred.type || 'public-key',
      id: base64urlToArrayBuffer(cred.id),
      transports: cred.transports || ['internal'],
    }));
  }

  return options;
}

/**
 * Extract credential data from the credential object for sending to server
 */
function extractRegistrationCredentialData(credential) {
  const response = credential.response;
  
  return {
    id: credential.id,
    rawId: arrayBufferToBase64url(credential.rawId),
    type: credential.type,
    response: {
      clientDataJSON: arrayBufferToBase64url(response.clientDataJSON),
      attestationObject: arrayBufferToBase64url(response.attestationObject),
    },
    clientExtensionResults: credential.getClientExtensionResults(),
  };
}

/**
 * Extract assertion data from the credential object for sending to server
 */
function extractAuthenticationCredentialData(credential) {
  const response = credential.response;
  
  return {
    id: credential.id,
    rawId: arrayBufferToBase64url(credential.rawId),
    type: credential.type,
    response: {
      clientDataJSON: arrayBufferToBase64url(response.clientDataJSON),
      authenticatorData: arrayBufferToBase64url(response.authenticatorData),
      signature: arrayBufferToBase64url(response.signature),
      userHandle: response.userHandle ? arrayBufferToBase64url(response.userHandle) : null,
    },
  };
}

/**
 * Start biometric credential registration
 * @param {Object} serverOptions - Options returned from /biometric/register/begin
 * @returns {Promise<Object>} - Credential data to send to /biometric/register/finish
 */
export async function registerBiometricCredential(serverOptions) {
  if (!isBiometricSupported()) {
    throw new Error('Biometric authentication is not supported on this device');
  }

  try {
    const options = prepareRegistrationOptions(serverOptions);
    
    const credential = await navigator.credentials.create({ publicKey: options });
    
    if (!credential) {
      throw new Error('Credential creation was cancelled');
    }

    return extractRegistrationCredentialData(credential);
  } catch (error) {
    throw toBiometricError('registration', error, serverOptions.rpId);
  }
}

/**
 * Perform biometric authentication
 * @param {Object} serverOptions - Options returned from /biometric/login/begin
 * @returns {Promise<Object>} - Credential assertion data to send to /biometric/login/finish
 */
export async function authenticateWithBiometric(serverOptions) {
  if (!isBiometricSupported()) {
    throw new Error('Biometric authentication is not supported on this device');
  }

  try {
    const options = prepareAuthenticationOptions(serverOptions);
    
    const credential = await navigator.credentials.get({ publicKey: options });
    
    if (!credential) {
      throw new Error('Authentication was cancelled');
    }

    return extractAuthenticationCredentialData(credential);
  } catch (error) {
    throw toBiometricError('authentication', error, serverOptions.rpId);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// REMEMBERED EMAIL - lets the login page show the biometric button without typing
// ─────────────────────────────────────────────────────────────────────────────

const BIOMETRIC_EMAIL_KEY = 'farm_smartly_biometric_email';

/** Email of the last user who registered / signed in with biometrics on this device (or null). */
export function getRememberedBiometricEmail() {
  try {
    return localStorage.getItem(BIOMETRIC_EMAIL_KEY) || null;
  } catch (e) {
    return null;
  }
}

export function rememberBiometricEmail(email) {
  try {
    if (email) localStorage.setItem(BIOMETRIC_EMAIL_KEY, email);
  } catch (e) {
    // storage unavailable (private mode): the button just falls back to appearing after typing the email
  }
}

export function forgetBiometricEmail() {
  try {
    localStorage.removeItem(BIOMETRIC_EMAIL_KEY);
  } catch (e) {
    // ignore
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// OFFLINE SUPPORT - Cache registration options
// ─────────────────────────────────────────────────────────────────────────────

const REGISTRATION_OPTIONS_KEY = 'farm_smartly_biometric_registration_options';
const REGISTRATION_OPTIONS_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * Store registration options in localStorage for offline/retry support
 * @param {Object} options - Server registration options
 */
export function cacheRegistrationOptions(options) {
  try {
    const data = {
      options,
      timestamp: Date.now(),
    };
    localStorage.setItem(REGISTRATION_OPTIONS_KEY, JSON.stringify(data));
  } catch (e) {
    console.warn('Failed to cache registration options:', e);
  }
}

/**
 * Get cached registration options if still valid
 * @returns {Object|null} - Cached options or null if expired/missing
 */
export function getCachedRegistrationOptions() {
  try {
    const data = localStorage.getItem(REGISTRATION_OPTIONS_KEY);
    if (!data) return null;
    
    const parsed = JSON.parse(data);
    const age = Date.now() - parsed.timestamp;
    
    if (age > REGISTRATION_OPTIONS_TTL) {
      localStorage.removeItem(REGISTRATION_OPTIONS_KEY);
      return null;
    }
    
    return parsed.options;
  } catch (e) {
    return null;
  }
}

/**
 * Clear cached registration options
 */
export function clearCachedRegistrationOptions() {
  try {
    localStorage.removeItem(REGISTRATION_OPTIONS_KEY);
  } catch (e) {
    // ignore
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// QR CODE GENERATION - Cross-device registration
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Generate a QR code URL for cross-device registration
 * Creates a URL that can be scanned on a mobile device to complete registration
 * @param {Object} registrationData - Contains challenge, userId, and temp token
 * @returns {string} - QR code data URL
 */
export async function generateRegistrationQRCode(registrationData) {
  // Create a compact registration URL
  const data = {
    v: 1, // version
    c: registrationData.challenge,
    u: registrationData.userId,
    t: registrationData.tempToken,
    r: window.location.origin,
  };
  
  const jsonString = JSON.stringify(data);
  const base64Data = btoa(jsonString)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
  
  const registrationUrl = `${window.location.origin}/biometric/register-qr?d=${base64Data}`;
  
  // Return the URL - actual QR code image generation would use a library like qrcode.js
  return registrationUrl;
}

/**
 * Parse QR code data from cross-device registration URL
 * @param {string} base64Data - The data parameter from the QR code URL
 * @returns {Object|null} - Parsed registration data
 */
export function parseRegistrationQRCode(base64Data) {
  try {
    const jsonString = atob(
      base64Data
        .replace(/-/g, '+')
        .replace(/_/g, '/')
        .padEnd(base64Data.length + (4 - (base64Data.length % 4)) % 4, '=')
    );
    return JSON.parse(jsonString);
  } catch (e) {
    console.error('Failed to parse QR code data:', e);
    return null;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// ERROR HANDLING
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Get a friendly error message for WebAuthn errors
 * @param {Error} error 
 * @returns {string}
 */
export function getBiometricErrorMessage(error) {
  if (!error) return 'Unknown error';
  
  if (error.name === 'NotAllowedError') {
    return 'Biometric authentication was cancelled or not allowed by the device';
  }
  if (error.name === 'SecurityError') {
    return 'Biometric authentication failed for security reasons (invalid origin or domain mismatch)';
  }
  if (error.name === 'NotSupportedError') {
    return 'Your device does not support biometric authentication';
  }
  if (error.name === 'InvalidStateError') {
    return 'This biometric credential is already registered or invalid';
  }
  if (error.name === 'AbortError') {
    return 'Biometric authentication was cancelled';
  }
  if (error.name === 'NetworkError') {
    return 'Network error. Please check your connection and try again.';
  }
  if (error.name === 'TimeoutError') {
    return 'Biometric authentication timed out. Please try again.';
  }
  
  return error.message || 'Biometric authentication failed';
}

/**
 * Check if error is retryable (network issues, timeouts)
 * @param {Error} error
 * @returns {boolean}
 */
export function isRetryableError(error) {
  return error && (
    error.name === 'NetworkError' ||
    error.name === 'TimeoutError' ||
    error.message?.includes('network') ||
    error.message?.includes('timeout')
  );
}
